import { FieldErrors, useFormContext } from 'react-hook-form'
import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { useTranslation } from 'react-i18next'
import { BetriebsstelleField, BetriebsstellenartField, Input } from '../../form'
import { useMemo } from 'react'
import BorderedBox from '../Generic/BorderedBox'

export interface Auftrag7_20FormProps {
  readOnly: boolean
  fieldPrefix: string
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  errors?: FieldErrors
}

export default function Auftrag7_20Form({
  readOnly,
  fieldPrefix,
  'data-testid': testId,
  activateEmptyFieldWarnings,
  errors
}: Readonly<Auftrag7_20FormProps>) {
  const formContext = useFormContext()

  const { control, formState, watch, resetField } = formContext
  const { t } = useTranslation('ui-befehle')
  const fieldPrefixLabel = 'auftraege.7_20'
  const [watchedBetriebsstellenart] = formContext.watch([
    `${fieldPrefixLabel}.betriebsstellenart231`
  ])

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const auftrag_7_20_active = watch('auftrag_7_20.enabled')

  return (
    <BefehlFormAuftrag
      auftragKennung="7.20"
      fieldName="auftrag_7_20.enabled"
      control={control}
      readOnly={readOnly}
      testId={testId}
      activateEmptyFieldWarnings={activateEmptyFieldWarnings}
      onChange={(checked) => {
        if (!checked) {
          resetField('auftrag_7_20')
        }
      }}
    >
      <span data-testid={`${testId}-text`}>{t('auftraege.7_20.label')}</span>
      <BorderedBox
        data-testid={`${testId}-feld-7-21-border-box`}
        footer={
          <span
            className={auftrag_7_20_active ? '' : 'text-gray-500 opacity-60'}
            data-testid={`${testId}-feld-7-21-label`}
          >
            {t(`${fieldPrefixLabel}.7_21_label`)}
          </span>
        }
      >
        <div className="w-full @container">
          {/* Line inputs */}
          <div className="flex flex-col @[400px]:flex-row gap-0.5">
            <div className="flex-1">
              <BefehlFormField
                name={`${fieldPrefix}.field_7_21.signal`}
                control={control}
                label={t(`${fieldPrefixLabel}.signal_label`)}
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-7-21-signal`}
                variant={'solid'}
                render={({ field: signalField }) => (
                  <Input
                    {...signalField}
                    label={t(`${fieldPrefixLabel}.signal_label`)}
                    className="w-full"
                    data-testid={`${testId}-feld-7-21-signal-inner`}
                    disabled={!auftrag_7_20_active}
                    errors={fieldErrors}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <BefehlFormField
                name={`${fieldPrefix}.field_7_21.zugmeldestelle.art`}
                control={control}
                label={t(`${fieldPrefixLabel}.betriebsstellenart_label`)}
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-7-21-zugmeldestelle-art`}
                variant="solid"
                render={({ field: bstTypField }) => (
                  <BetriebsstellenartField
                    {...bstTypField}
                    label={t(`${fieldPrefixLabel}.betriebsstellenart_label`)}
                    variant="solid"
                    className="w-full"
                    data-testid={`${testId}-feld-7-21-zugmeldestelle-art-inner`}
                    selectClassName="w-full"
                    disabled={!auftrag_7_20_active}
                    errors={fieldErrors}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <BefehlFormField
                name={`${fieldPrefix}.field_7_21.zugmeldestelle.name`}
                control={control}
                label={t(`${fieldPrefixLabel}.zugmeldestelle_label`)}
                readOnly={readOnly}
                activateEmptyWarning={activateEmptyFieldWarnings}
                data-testid={`${testId}-feld-7-21-zugmeldestelle-name`}
                variant="solid"
                render={({ field: zmField }) => (
                  <BetriebsstelleField
                    {...zmField}
                    label={t(`${fieldPrefixLabel}.zugmeldestelle_label`)}
                    className="w-full"
                    data-testid={`${testId}-feld-7-21-zugmeldestelle-name-inner`}
                    disabled={!auftrag_7_20_active}
                    typ={watchedBetriebsstellenart}
                    errors={fieldErrors}
                  />
                )}
              />
            </div>
          </div>
          <div></div>
        </div>
      </BorderedBox>
    </BefehlFormAuftrag>
  )
}
